
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence, Reorder, useDragControls } from 'framer-motion';
import { Trophy, ChevronLeft, ChevronRight, ChevronDown, ListOrdered, Calendar, X, Check, Settings2, ArrowRight, Loader2, ArrowLeft, Plus, Search, Globe, Sparkles, LayoutGrid, Menu, ShieldCheck, BrainCircuit, GripVertical } from 'lucide-react';
import { fetchLeagueFixtures, fetchLeagues, searchLeagues, fetchUserLeagueSelectionsFromDB, saveUserLeagueSelectionsToDB, LeagueDB, fetchLeaguesByIds, getLeagueSeason, getLeagueSeasonLabel } from '../../services/footballService';
import { fetchGlobalHighlights } from '../../services/highlightService';
import { HighlightItem } from '../../types';
import { LoadingBar } from '../Layout/LoadingBar';
import { FullPageLoader } from '../Common/FullPageLoader';
import { Game } from '../../types';
import { StandingsModal } from '../Games/StandingsModal';
import { GameCard } from '../Games/GameCard';
import { LeagueSelectionContent } from '../Games/LeagueSelectionContent';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { useDebounce } from '../../hooks/useDebounce';
import { useToast } from '../../context/ToastContext';
import { t } from '../../i18n';
import { getLeagueName, LEAGUE_TRANSLATIONS } from '../../services/leagueService';
import SEO from '../Common/SEO';

const UNKNOWN_SESSION_TTL = 3 * 60 * 1000; 
const LOCAL_SELECTION_KEY = 'xleven_league_session';
const LAST_VIEWED_LEAGUE_KEY = 'xleven_last_viewed_league';

const getLocalISODate = (date: Date = new Date()) => {
    return date.toLocaleDateString('sv-SE');
};

const MAJOR_LEAGUE_IDS = ['39', '140', '135', '78', '61', '292', '233', '235', '236', '88', '94', '203', '253', '307', '40', '98', '71', '262', '128', '144', '179'];
const MAJOR_CUP_IDS = ['1', '2', '3', '848', '5', '9', '10', '11', '45', '48', '81', '143', '137', '66', '202'];

// Define missing interface for GamesView props
interface GamesViewProps {
    selectedLeagueId?: string;
    onNavigateToGame: (game: Game) => void;
    savedState: { leagueId: string, date: string } | null;
    onSaveState: (state: { leagueId: string, date: string } | null) => void;
    onOpenSidebar: () => void;
}

export const GamesView: React.FC<GamesViewProps> = ({ selectedLeagueId, onNavigateToGame, savedState, onSaveState, onOpenSidebar }) => {
    const { lang } = useLanguage();
    const { user, isLoading: isAuthLoading } = useAuth();
    const { showToast } = useToast();
    const [loading, setLoading] = useState(true);
    const [allGames, setAllGames] = useState<Game[]>([]);
    const [leagues, setLeagues] = useState<LeagueDB[]>([]);
    const [extraLeagues, setExtraLeagues] = useState<LeagueDB[]>([]); 
    const [scoutedResults, setScoutedResults] = useState<LeagueDB[]>([]);
    const [highlights, setHighlights] = useState<HighlightItem[]>([]);
    const [leaguesLoading, setLeaguesLoading] = useState(true);
    const [isSearching, setIsSearching] = useState(false);
    const [leagueSearchQuery, setLeagueSearchQuery] = useState('');
    const debouncedSearchQuery = useDebounce(leagueSearchQuery, 500);
    const [selectedLeagueIds, setSelectedLeagueIds] = useState<string[]>([]);
    const [isSelectingLeagues, setIsSelectingLeagues] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isModalAnimating, setIsModalAnimating] = useState(false); 
    const [isSaving, setIsSaving] = useState(false);
    const [activeLeagueId, setActiveLeagueId] = useState(selectedLeagueId || savedState?.leagueId || localStorage.getItem(LAST_VIEWED_LEAGUE_KEY) || 'all');
    const [pendingLeagueId, setPendingLeagueId] = useState(activeLeagueId);
    const [selectedDate, setSelectedDate] = useState<string>(savedState?.date || getLocalISODate());
    const [viewDate, setViewDate] = useState<Date>(new Date(selectedDate || getLocalISODate()));
    const [isLeagueSwitcherOpen, setIsLeagueSwitcherOpen] = useState(false);
    const [isSwitcherAnimating, setIsSwitcherAnimating] = useState(false);
    const [isStandingsOpen, setIsStandingsOpen] = useState(false);
    const [standingsLeague, setStandingsLeague] = useState<{id: string, name: string, logo: string} | null>(null);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [isDateAnimating, setIsDateAnimating] = useState(false);
    const [pickerMonth, setPickerMonth] = useState<Date>(new Date(selectedDate));
    const [lastConfirmedIds, setLastConfirmedIds] = useState<string[]>([]);
    const contentRef = useRef<HTMLDivElement>(null);
    const [orderedLeagues, setOrderedLeagues] = useState<LeagueDB[]>([]);
    const [tempOrderedLeagues, setTempOrderedLeagues] = useState<LeagueDB[]>([]);
    const [swipeDirection, setSwipeDirection] = useState(0);
    const stripRef = useRef<HTMLDivElement>(null);

    const activeSeason = useMemo(() => {
        return getLeagueSeason(activeLeagueId, viewDate);
    }, [viewDate, activeLeagueId]);

    const leaguePool = useMemo(() => {
        const poolMap = new Map<string, LeagueDB>();
        leagues.forEach(l => poolMap.set(l.id, l));
        extraLeagues.forEach(l => poolMap.set(l.id, l));
        return Array.from(poolMap.values());
    }, [leagues, extraLeagues]);

    const activeLeague = useMemo(() => {
        return leaguePool.find(l => l.id === activeLeagueId) || leagues[0];
    }, [leaguePool, activeLeagueId, leagues]);

    const displayedGames = useMemo(() => allGames.filter(g => g.dateLabel === selectedDate), [allGames, selectedDate]);

    const groupedGames = useMemo(() => {
        if (activeLeagueId !== 'all') return displayedGames.length > 0 ? { [activeLeagueId]: displayedGames } : {};

        const groups: Record<string, Game[]> = {};
        
        // 정렬된 리그 순서대로 그룹을 만듭니다.
        const orderMap = new Map(orderedLeagues.map((l, i) => [String(l.id), i]));
        
        // 정렬
        const sortedGames = [...displayedGames]
            .sort((a, b) => {
                const orderA = orderMap.has(String(a.league)) ? orderMap.get(String(a.league))! : 999;
                const orderB = orderMap.has(String(b.league)) ? orderMap.get(String(b.league))! : 999;
                return orderA - orderB;
            });

        sortedGames.forEach(game => {
            const round = game.round || 'Unknown Round';
            const key = `${game.league}-${round}`;
            if (!groups[key]) groups[key] = [];
            groups[key].push(game);
        });
        return groups;
    }, [displayedGames, activeLeagueId, orderedLeagues]);

    const loadConfirmedSelections = async (currentUser: any) => {
        let confirmedSelections: { league_id: string, order: number }[] = [];
        if (currentUser) {
            confirmedSelections = await fetchUserLeagueSelectionsFromDB(currentUser.id);
        } else {
            const sessionStr = localStorage.getItem(LOCAL_SELECTION_KEY);
            if (sessionStr) {
                const session = JSON.parse(sessionStr);
                const now = Date.now();
                if (now - session.timestamp < UNKNOWN_SESSION_TTL) {
                    confirmedSelections = session.selections || [];
                }
            }
        }
        return confirmedSelections;
    };

    useEffect(() => {
        if (isAuthLoading) return;

        const initSelectionProcess = async () => {
            setLeaguesLoading(true);
            const allLeagues = await fetchLeagues();
            setLeagues(allLeagues);
            const confirmedSelections = await loadConfirmedSelections(user);
            
            const selectedIds = confirmedSelections.map(s => s.league_id);
            setSelectedLeagueIds(selectedIds);
            setLastConfirmedIds(selectedIds);

            const mainIds = allLeagues.map(l => l.id);
            const extraIds = selectedIds.filter(id => !mainIds.includes(id));
            
            let currentExtraLeagues = [];
            if (extraIds.length > 0) {
                try {
                    currentExtraLeagues = await fetchLeaguesByIds(extraIds);
                    setExtraLeagues(currentExtraLeagues);
                } catch (e) { console.error(e); }
            }

            const pool = [...allLeagues, ...currentExtraLeagues];
            const filteredPool = pool.filter((l, idx, self) => selectedIds.includes(l.id) && self.findIndex(t => t.id === l.id) === idx);
            
            const ordered = confirmedSelections
                .map(s => filteredPool.find(p => p.id === s.league_id))
                .filter(Boolean) as LeagueDB[];
            
            setOrderedLeagues(ordered);

            if (selectedIds.length > 0) {
                setIsSelectingLeagues(false);
                if (!selectedLeagueId && !savedState?.leagueId) setActiveLeagueId('all');
            } else {
                setIsSelectingLeagues(true);
            }
            setLeaguesLoading(false);
            setIsInitialLoading(false);
        };
        initSelectionProcess();
    }, [user, isAuthLoading, selectedLeagueId]);

    useEffect(() => {
        if (debouncedSearchQuery.length >= 1) {
            setIsSearching(true);

            // 1. Search locally in LEAGUE_TRANSLATIONS
            const q = debouncedSearchQuery.toLowerCase();
            const localMatches = LEAGUE_TRANSLATIONS.filter(t => 
                Object.values(t.names).some(name => (name || '').toLowerCase().includes(q))
            );

            const localResults: LeagueDB[] = localMatches.map(m => {
                const existing = leaguePool.find(l => l.id === m.league_id.toString());
                if (existing) return existing;
                return {
                    id: m.league_id.toString(),
                    name: m.names.en || 'Unknown League',
                    logo_url: `https://media.api-sports.io/football/leagues/${m.league_id}.png`
                };
            });

            setScoutedResults(localResults);

            // 2. Search via API
            searchLeagues(debouncedSearchQuery).then(apiResults => {
                setScoutedResults(prev => {
                    const combined = [...prev];
                    if (Array.isArray(apiResults)) {
                        apiResults.forEach(apiL => {
                            if (!combined.find(l => l.id === apiL.id)) {
                                combined.push(apiL);
                            }
                        });
                    }
                    return combined;
                });
                setIsSearching(false);
            }).catch(() => {
                setIsSearching(false);
            });
        } else {
            setScoutedResults([]);
        }
    }, [debouncedSearchQuery, leaguePool]);

    useEffect(() => {
        if (onSaveState && !isSelectingLeagues && !leaguesLoading) onSaveState({ leagueId: activeLeagueId, date: selectedDate });
    }, [activeLeagueId, selectedDate, onSaveState, isSelectingLeagues, leaguesLoading]);

    const [initialLoadDone, setInitialLoadDone] = useState(false);

    const loadFixtures = async (leagueId: string, season: string, confirmedIds: string[], targetDate?: string, isReturningFromDetail?: boolean) => {
        if (isInitialLoading || leaguesLoading) return;
        
        const dateToLoad = targetDate || selectedDate;
        const today = getLocalISODate();
        
        // 상세에서 뒤로가기(혹은 이미 렌더링된 상태)이고, 오늘 날짜가 아니며, 데이터가 이미 있다면 재호출 생략
        if (isReturningFromDetail && dateToLoad !== today && allGames.length > 0) {
            return;
        }

        setLoading(true);
        try {
            if (leagueId === 'all') {
                if (confirmedIds.length === 0) {
                    setAllGames([]);
                } else {
                    const chunkSize = 5;
                    const results = [];
                    for (let i = 0; i < confirmedIds.length; i += chunkSize) {
                        const chunk = confirmedIds.slice(i, i + chunkSize);
                        const chunkResults = await Promise.all(
                            chunk.map(id => {
                                const dynamicSeason = getLeagueSeason(id, viewDate);
                                return fetchLeagueFixtures(id, dynamicSeason).catch(e => {
                                    console.error(`Failed to fetch fixtures for league ${id}`, e);
                                    return [];
                                });
                            })
                        );
                        results.push(...chunkResults.flat());
                        
                        if (i + chunkSize < confirmedIds.length) {
                            await new Promise(resolve => setTimeout(resolve, 100));
                        }
                    }
                    setAllGames(results);
                }
            } else {
                const data = await fetchLeagueFixtures(leagueId, season);
                setAllGames(data);
            }
        } catch (err) {
            console.error("Failed to fetch fixtures:", err);
        } finally {
            setLoading(false);
            setInitialLoadDone(true);
        }
    };

    useEffect(() => {
        const loadHighlights = async () => {
            const h = await fetchGlobalHighlights();
            setHighlights(h);
        };
        loadHighlights();
    }, []);

    useEffect(() => {
        if (isInitialLoading || (isSelectingLeagues && lastConfirmedIds.length === 0) || leaguesLoading) return;
        // initialLoadDone이 true이면 이미 한 번 로드된 것이므로(뒤로가기 등) isReturningFromDetail 플래그를 넘김
        loadFixtures(activeLeagueId, activeSeason, lastConfirmedIds, selectedDate, initialLoadDone);
    }, [activeLeagueId, activeSeason, leaguesLoading, lastConfirmedIds]);

    useEffect(() => {
        if (isSelectingLeagues && lastConfirmedIds.length === 0) return;
        const scrollToSelected = () => {
            if (stripRef.current) {
                const selectedBtn = document.getElementById(`date-${selectedDate}`);
                if (selectedBtn) selectedBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            }
        };
        const timer = setTimeout(scrollToSelected, 150);
        return () => clearTimeout(timer);
    }, [selectedDate, loading, isSelectingLeagues, lastConfirmedIds.length]);

    useEffect(() => {
        if (isSelectingLeagues) {
            const timer = setTimeout(() => setIsModalAnimating(true), 10);
            return () => clearTimeout(timer);
        } else {
            setIsModalAnimating(false);
        }
    }, [isSelectingLeagues]);

    useEffect(() => {
        if (isLeagueSwitcherOpen) {
            setPendingLeagueId(activeLeagueId); 
            // 모달이 열릴 때 현재 확정된 순서로 임시 상태 초기화
            setTempOrderedLeagues([...orderedLeagues]);
            const timer = setTimeout(() => setIsSwitcherAnimating(true), 10);
            return () => clearTimeout(timer);
        } else {
            setIsSwitcherAnimating(false);
        }
    }, [isLeagueSwitcherOpen, activeLeagueId, orderedLeagues]);

    useEffect(() => {
        if (isDatePickerOpen) {
            setPickerMonth(new Date(selectedDate));
            const timer = setTimeout(() => setIsDateAnimating(true), 10);
            return () => clearTimeout(timer);
        } else {
            setIsDateAnimating(false);
        }
    }, [isDatePickerOpen, selectedDate]);

    const handleLeagueToggle = (league: LeagueDB) => {
        const lid = league.id;
        const isSelected = selectedLeagueIds.includes(lid);
        
        if (!isSelected) {
            const isAdmin = user?.role === 'admin';
            if (!isAdmin) {
                const maxLeagues = user ? 10 : 1;
                if (selectedLeagueIds.length >= maxLeagues) {
                    showToast('info', user ? t(lang, 'maxLeaguesReached') : t(lang, 'maxLeaguesReachedGuest'));
                    return;
                }
            }

            const isMain = leagues.find(l => l.id === lid);
            if (!isMain) setExtraLeagues(prev => prev.find(p => p.id === lid) ? prev : [...prev, league]);
            setSelectedLeagueIds(prev => [...prev, lid]);
        } else {
            setSelectedLeagueIds(prev => prev.filter(id => id !== lid));
        }
    };

    const handleSelectionDone = async () => {
        if (selectedLeagueIds.length === 0) { alert(t(lang, 'selectAtLeastOneLeague')); return; }
        setIsSaving(true);
        try {
            // orderedLeagues의 순서를 기반으로 하되, selectedLeagueIds에 있는 것들만 필터링하고 새로 추가된 것은 끝에 붙입니다.
            const newOrdered = [...orderedLeagues.filter(l => selectedLeagueIds.includes(l.id))];
            selectedLeagueIds.forEach(id => {
                if (!newOrdered.find(l => l.id === id)) {
                    const league = leaguePool.find(l => l.id === id);
                    if (league) newOrdered.push(league);
                }
            });

            const currentSelections = newOrdered.map((l, index) => ({ league_id: l.id, order: index }));
            
            if (user) await saveUserLeagueSelectionsToDB(user.id, currentSelections);
            else {
                const sessionData = { selections: currentSelections, timestamp: Date.now() };
                localStorage.setItem(LOCAL_SELECTION_KEY, JSON.stringify(sessionData));
            }
            
            setLastConfirmedIds(selectedLeagueIds);
            setOrderedLeagues(newOrdered);
            
            let nextLeagueId = activeLeagueId;
            if (!selectedLeagueIds.includes(activeLeagueId) && activeLeagueId !== 'all') {
                nextLeagueId = 'all';
                setActiveLeagueId('all');
                const today = getLocalISODate();
                setSelectedDate(today);
                setViewDate(new Date(today));
            }
            
            await loadFixtures(nextLeagueId, activeSeason, selectedLeagueIds);
            handleCloseSelection();
            setLeagueSearchQuery(''); 
        } catch (e) { alert(t(lang, 'syncFailedTryAgain')); } 
        finally { setIsSaving(false); }
    };

    const handleReorder = (newOrder: LeagueDB[]) => {
        setTempOrderedLeagues(newOrder);
    };

    const handleConfirmActiveLeague = async () => {
        setIsSaving(true);
        try {
            const currentSelections = tempOrderedLeagues.map((l, index) => ({ league_id: l.id, order: index }));
            if (user) await saveUserLeagueSelectionsToDB(user.id, currentSelections);
            else {
                const sessionData = { selections: currentSelections, timestamp: Date.now() };
                localStorage.setItem(LOCAL_SELECTION_KEY, JSON.stringify(sessionData));
            }
            
            // lastConfirmedIds 업데이트하여 필요시 데이터 새로고침 유도
            const selectedIds = tempOrderedLeagues.map(l => l.id);
            setLastConfirmedIds(selectedIds);
            setSelectedLeagueIds(selectedIds);
            setOrderedLeagues([...tempOrderedLeagues]);

            setActiveLeagueId(pendingLeagueId);
            localStorage.setItem(LAST_VIEWED_LEAGUE_KEY, pendingLeagueId);
            window.navigateTo(`/games/${pendingLeagueId}`);
            const today = getLocalISODate();
            setSelectedDate(today);
            setViewDate(new Date(today));
            closeLeagueSwitcher();
        } catch (e) {
            console.error("Failed to save reordered leagues:", e);
            alert(t(lang, 'syncFailedTryAgain'));
        } finally {
            setIsSaving(false);
        }
    };

    const scrollToTop = useCallback(() => {
        // 1. Try scrolling the immediate content ref
        if (contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
        // 2. Try scrolling the main scroll container in App.tsx (main > div)
        const mainScrollContainer = document.querySelector('main > div');
        if (mainScrollContainer) {
            mainScrollContainer.scrollTo({ top: 0, behavior: 'instant' });
        }
        // 3. Global window scroll just in case
        window.scrollTo({ top: 0, behavior: 'instant' });
    }, []);

    const handleDateSelect = (fullDate: string) => {
        const current = new Date(selectedDate).getTime();
        const next = new Date(fullDate).getTime();
        setSwipeDirection(next > current ? 1 : -1);
        setSelectedDate(fullDate);
        setViewDate(new Date(fullDate));
        closeDatePicker();
        scrollToTop();
    };

    const handleMonthChange = (offset: number) => {
        const nextViewDate = new Date(viewDate);
        nextViewDate.setMonth(nextViewDate.getMonth() + offset);
        const today = new Date();
        const isTargetMonthToday = nextViewDate.getFullYear() === today.getFullYear() && nextViewDate.getMonth() === today.getMonth();
        const nextSelected = isTargetMonthToday 
            ? getLocalISODate(today) 
            : getLocalISODate(new Date(nextViewDate.getFullYear(), nextViewDate.getMonth(), 1));
        setSelectedDate(nextSelected);
        setViewDate(nextViewDate);
    };

    const closeLeagueSwitcher = () => { setIsSwitcherAnimating(false); setTimeout(() => setIsLeagueSwitcherOpen(false), 500); };
    const closeDatePicker = () => { setIsDateAnimating(false); setTimeout(() => setIsDatePickerOpen(false), 500); };
    const handleCloseSelection = () => { setIsModalAnimating(false); setTimeout(() => setIsSelectingLeagues(false), 500); };
    const formatRoundLabel = (round?: string) => {
        if (!round) return null;
        const raw = round.includes('-') ? round.split('-').pop()?.trim() : round;
        if (/^\d+$/.test(raw || '')) return lang === 'ko' ? `라운드 ${raw}` : `Round ${raw}`;
        return raw;
    };

    // Inline component for Reorder.Item to correctly use useDragControls
    const LeagueReorderItem = ({ league, pendingLeagueId, onSelect }: { league: LeagueDB, pendingLeagueId: string, onSelect: (id: string) => void }) => {
        const controls = useDragControls();
        return (
            <Reorder.Item 
                value={league}
                dragListener={false}
                dragControls={controls}
                className="relative"
            >
                <div className={`relative flex items-center gap-4 p-4 rounded-2xl transition-all border ${pendingLeagueId === league.id ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-100 md:hover:border-slate-200 md:hover:bg-slate-50 text-slate-900'}`}>
                    <div className="cursor-pointer flex-1 flex items-center gap-4 min-w-0" onClick={() => onSelect(league.id)}>
                        {pendingLeagueId === league.id && <div className="absolute left-0 top-3 bottom-3 w-1 bg-rose-500 rounded-r-full" />}
                        <div className="w-10 h-10 rounded-xl bg-white p-1.5 flex items-center justify-center shrink-0 border border-slate-100 shadow-sm">
                            <img src={league.id === '1' ? "/assets/fiwc.png" : league.logo_url} alt="" className="w-full h-full object-contain" />
                        </div>
                        <div className="flex-1 text-left min-w-0">
                            <span className={`font-black text-xs uppercase tracking-tight truncate block ${pendingLeagueId === league.id ? 'text-white' : 'text-slate-900'}`}>
                                {getLeagueName(parseInt(league.id), lang, league.name)}
                            </span>
                        </div>
                        {pendingLeagueId === league.id && <Check size={16} strokeWidth={3} className="text-rose-500 shrink-0" />}
                    </div>
                    <div 
                        className="p-1 cursor-grab active:cursor-grabbing text-slate-300 touch-none"
                        onPointerDown={(e) => controls.start(e)}
                    >
                        <GripVertical size={20} />
                    </div>
                </div>
            </Reorder.Item>
        );
    };

    const daysInMonthStrip = useMemo(() => new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate(), [viewDate]);
    const stripDates = useMemo(() => Array.from({length: daysInMonthStrip}, (_, i) => { const d = new Date(viewDate.getFullYear(), viewDate.getMonth(), i + 1); return { fullDate: getLocalISODate(d), day: i + 1, weekday: d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase() }; }), [daysInMonthStrip, viewDate]);

    const seoTitle = useMemo(() => {
        if (activeLeagueId === 'all') return t(lang, 'allMatchFixturesSeoTitle');
        const leagueName = getLeagueName(parseInt(activeLeague?.id || '0'), lang, activeLeague?.name);
        return `${leagueName} ${t(lang, 'leagueMatchFixturesSeoTitle')}`;
    }, [activeLeagueId, activeLeague, lang]);

    const seoDescription = useMemo(() => {
        if (activeLeagueId === 'all') return t(lang, 'allMatchFixturesSeoDesc');
        const leagueName = getLeagueName(parseInt(activeLeague?.id || '0'), lang, activeLeague?.name);
        return t(lang, 'leagueMatchFixturesSeoDesc').replace('{{leagueName}}', leagueName);
    }, [activeLeagueId, activeLeague, lang]);

    if (isInitialLoading || isAuthLoading) return <FullPageLoader isLoading={true} icon={Globe} text={t(lang, 'loadingLeagues')} />;

    if (isSelectingLeagues && lastConfirmedIds.length === 0) {
        return (
            <div className="min-h-screen bg-white pb-32 animate-in fade-in duration-500">
                <SEO 
                    title={seoTitle}
                    description={seoDescription}
                />
                <LeagueSelectionContent 
                    isInitial={true}
                    lang={lang}
                    leagueSearchQuery={leagueSearchQuery}
                    setLeagueSearchQuery={setLeagueSearchQuery}
                    isSearching={isSearching}
                    scoutedResults={scoutedResults}
                    leaguePool={leaguePool}
                    selectedLeagueIds={selectedLeagueIds}
                    handleLeagueToggle={handleLeagueToggle}
                    handleSelectionDone={handleSelectionDone}
                    handleCloseSelection={handleCloseSelection}
                    isSaving={isSaving}
                    MAJOR_LEAGUE_IDS={MAJOR_LEAGUE_IDS}
                    MAJOR_CUP_IDS={MAJOR_CUP_IDS}
                />
            </div>
        );
    }

    const handleOpenStandings = () => {
        if (activeLeagueId === 'all') {
            // "모든 리그" 페이지에서는 프리미어 리그(39)를 기본으로 보여주거나 첫 번째 리그를 보여줌
            const firstLeague = orderedLeagues[0] || leagues[0];
            setStandingsLeague({
                id: firstLeague?.id || '39',
                name: firstLeague?.name || 'Premier League',
                logo: firstLeague?.logo_url || 'https://media.api-sports.io/football/leagues/39.png'
            });
        } else {
            setStandingsLeague({
                id: activeLeagueId,
                name: activeLeague?.name || '',
                logo: activeLeague?.logo_url || ''
            });
        }
        setIsStandingsOpen(true);
    };

    return (
        <div className="min-h-screen bg-white relative">
            <SEO 
                title={seoTitle}
                description={seoDescription}
            />
            <LoadingBar isLoading={loading} />
            <StandingsModal 
                isOpen={isStandingsOpen} 
                onClose={() => setIsStandingsOpen(false)} 
                leagueId={standingsLeague?.id || activeLeagueId} 
                leagueName={standingsLeague?.name || (activeLeagueId === 'all' ? t(lang, 'allLeagues') : (activeLeague?.name || ''))} 
                leagueLogo={standingsLeague?.logo || (activeLeagueId === 'all' ? '' : (activeLeague?.logo_url || ''))} 
                season={activeSeason}
                onLeagueChange={(id, name, logo) => setStandingsLeague({ id, name, logo })}
            />
            <header className="sticky top-0 z-40 bg-white border-b border-gray-50 px-4 h-16 md:h-20 flex items-center justify-between">
                <div className="flex items-center gap-2 md:gap-4 flex-1">
                    <button onClick={() => setIsLeagueSwitcherOpen(true)} className="bg-slate-900 text-white px-4 py-2 md:px-5 md:py-2.5 rounded-2xl flex items-center gap-3 md:hover:bg-rose-600 transition-all active:scale-95 shadow-lg shadow-rose-50 group min-w-0">
                        <div className="w-6 h-6 md:w-8 md:h-8 rounded-lg bg-white p-1 flex items-center justify-center shrink-0">
                            {activeLeagueId === 'all' ? (
                                <Globe size={18} className="text-slate-900" />
                            ) : (
                                <img src={activeLeagueId === '1' ? "/assets/fiwc.png" : activeLeague?.logo_url} alt="" className="w-full h-full object-contain" />
                            )}
                        </div>
                        <div className="flex flex-col items-start min-w-0">
                            <span className="text-[11px] md:text-sm font-black uppercase tracking-tight truncate w-full">
                                {activeLeagueId === 'all' ? t(lang, 'allLeagues') : getLeagueName(parseInt(activeLeague?.id || '0'), lang, activeLeague?.name)}
                            </span>
                            <span className="text-[7px] md:text-[8px] font-bold text-white/50 uppercase tracking-widest leading-none">
                                {activeLeagueId === 'all' ? t(lang, 'season2526') : getLeagueSeasonLabel(activeLeagueId, activeSeason)}
                            </span>
                        </div>
                        <ChevronDown size={14} className="text-rose-400 md:group-hover:translate-y-0.5 transition-transform shrink-0" />
                    </button>
                </div>
                <div className="flex items-center gap-1.5 md:gap-3">
                    <button onClick={handleOpenStandings} className="p-2.5 md:p-3 text-slate-900 bg-slate-50 md:hover:bg-rose-50 md:hover:text-rose-500 rounded-xl transition-all">
                        <ListOrdered size={20} />
                    </button>
                    <button onClick={() => setIsSelectingLeagues(true)} className="p-2.5 md:p-3 text-slate-400 md:hover:bg-slate-100 md:hover:text-slate-900 rounded-xl transition-all">
                        <Settings2 size={20} />
                    </button>
                </div>
            </header>
            <div className="bg-white px-6 py-4 flex items-center justify-between border-b border-gray-50"><div className="w-full flex items-center justify-between gap-1 bg-slate-50 rounded-[20px] p-1.5 border border-slate-100 shadow-inner"><button onClick={() => handleMonthChange(-1)} className="p-2 text-slate-400 md:hover:text-slate-900 transition-colors bg-white rounded-xl shadow-sm"><ChevronLeft size={16}/></button><button onClick={() => setIsDatePickerOpen(true)} className="px-6 text-[11px] font-black text-slate-900 uppercase tracking-[0.2em]">{viewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</button><button onClick={() => handleMonthChange(1)} className="p-2 text-slate-400 md:hover:text-slate-900 transition-colors bg-white rounded-xl shadow-sm"><ChevronRight size={16}/></button></div></div>
            <div className="sticky top-[64px] md:top-[80px] z-10 bg-white border-b border-gray-100"><div ref={stripRef} className="flex overflow-x-auto no-scrollbar px-4 md:px-8 gap-3 py-6 snap-x w-full">{stripDates.map(({ fullDate, day, weekday }) => { const isSelected = fullDate === selectedDate; const hasGame = allGames.some(g => g.dateLabel === fullDate); return (<button id={`date-${fullDate}`} key={fullDate} onClick={() => { setSelectedDate(fullDate); setViewDate(new Date(fullDate)); scrollToTop(); }} className={`flex flex-col items-center justify-center min-w-[56px] md:min-w-[80px] h-16 md:h-24 rounded-[20px] md:rounded-3xl snap-center shrink-0 transition-all duration-300 ${isSelected ? 'bg-rose-500 text-white shadow-md shadow-rose-200 -translate-y-0.5 md:-translate-y-1' : 'bg-white text-gray-400 md:hover:bg-gray-50 border border-gray-100 shadow-sm'}`}><span className={`text-[8px] md:text-[10px] font-black mb-0.5 md:mb-1 ${isSelected ? 'text-white/80' : 'text-gray-400'}`}>{weekday}</span><span className={`text-lg md:text-2xl font-black leading-none ${isSelected ? 'text-white' : 'text-slate-900'}`}>{day}</span>{hasGame && <div className={`w-1 h-1 md:w-1.5 md:h-1.5 rounded-full mt-1 md:mt-2 ${isSelected ? 'bg-white animate-pulse' : 'bg-rose-500'}`}></div>}</button>); })}</div></div>
            

            <div ref={contentRef} className="max-w-4xl mx-auto px-2 py-10 pb-40">
                {loading ? (
                    <div className="py-32 flex flex-col items-center justify-center gap-4">
                        <div className="relative w-16 h-16">
                            <div className="absolute inset-0 border-4 border-slate-100 rounded-full"></div>
                            <div className="absolute inset-0 border-4 border-t-rose-500 rounded-full animate-spin"></div>
                        </div>
                    </div>
                ) : Object.keys(groupedGames).length === 0 ? (
                    <div className="text-center py-10 flex flex-col items-center">
                        <div className="w-24 h-24 bg-slate-50 rounded-[40px] flex items-center justify-center text-slate-200 mb-6 border border-slate-100 shadow-inner">
                            <Trophy size={48} strokeWidth={1.5} />
                        </div>
                        <h3 className="text-slate-900 font-black text-lg mb-1 uppercase tracking-tight">{t(lang, 'noSectorActivity')}</h3>
                        <p className="font-bold text-[10px] uppercase tracking-[0.2em] text-slate-400">{t(lang, 'noSectorActivityDesc')}</p>
                    </div>
                ) : (
                    <div className="space-y-8 animate-in fade-in duration-300">
                        {Object.entries(groupedGames).map(([key, games]) => {
                            const [leagueId, ...roundParts] = key.split('-');
                            const round = roundParts.join('-');
                            const league = leaguePool.find(l => l.id === leagueId);
                            const leagueGames = games as Game[];
                            const formattedRound = formatRoundLabel(round);
                            return (
                                <div key={key} className="space-y-4 border-b border-slate-100 pb-8 last:border-b-0">
                                    {activeLeagueId === 'all' && (
                                        <div className="flex items-center gap-4 px-4 py-2 bg-gradient-to-r from-slate-50 to-slate-100 rounded-[24px] border border-slate-200/50 shadow-sm mx-1">
                                            <div className="w-10 h-10 rounded-xl bg-white p-2 flex items-center justify-center border border-slate-200 shadow-sm">
                                                <img src={leagueId === '1' ? "/assets/fiwc.png" : league?.logo_url} alt="" className="w-full h-full object-contain" />
                                            </div>
                                            <div className="flex flex-row justify-between items-center flex-1">
                                                <h3 className="text-[12px] font-black text-slate-900 uppercase tracking-tight leading-tight">
                                                    {getLeagueName(parseInt(leagueId), lang, league?.name)}
                                                </h3>
                                                {formattedRound && <span className="text-[10px] font-bold text-rose-600 uppercase tracking-widest leading-none">{formattedRound}</span>}
                                            </div>
                                        </div>
                                    )}
                                    <div className="space-y-4">
                                        {leagueGames.map(game => (
                                            <GameCard 
                                                key={game.id} 
                                                game={{
                                                    ...game,
                                                    hasHighlight: highlights.some(h => h.match_id === game.id)
                                                } as any}
                                                lang={lang} 
                                                onClick={onNavigateToGame} 
                                            />
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            {isSelectingLeagues && (
                <div className={`fixed inset-0 z-[300] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-500 ${isModalAnimating ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={handleCloseSelection}></div>
                    <div className={`relative bg-white w-full h-[90vh] md:h-[85vh] md:max-w-2xl md:mx-auto rounded-t-[48px] md:rounded-[48px] shadow-sm flex flex-col overflow-hidden transition-transform duration-700 ease-[0.32,0.72,0,1] ${isModalAnimating ? 'translate-y-0 md:scale-100' : 'translate-y-full md:scale-95'}`}>
                        <div className="w-12 h-1.5 bg-slate-100 rounded-full mx-auto my-4 shrink-0 md:hidden"></div>
                        <div className="flex-1 overflow-hidden">
                            <LeagueSelectionContent 
                                isInitial={false}
                                lang={lang}
                                leagueSearchQuery={leagueSearchQuery}
                                setLeagueSearchQuery={setLeagueSearchQuery}
                                isSearching={isSearching}
                                scoutedResults={scoutedResults}
                                leaguePool={leaguePool}
                                selectedLeagueIds={selectedLeagueIds}
                                handleLeagueToggle={handleLeagueToggle}
                                handleSelectionDone={handleSelectionDone}
                                handleCloseSelection={handleCloseSelection}
                                isSaving={isSaving}
                                MAJOR_LEAGUE_IDS={MAJOR_LEAGUE_IDS}
                                MAJOR_CUP_IDS={MAJOR_CUP_IDS}
                            />
                        </div>
                    </div>
                </div>
            )}
            {isDatePickerOpen && (
                <div className={`fixed inset-0 z-[300] flex items-end md:items-center justify-center transition-all duration-500 ${isDateAnimating ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={closeDatePicker}></div>
                    <div className={`relative bg-white w-full md:max-w-sm rounded-t-[40px] md:rounded-[32px] shadow-sm overflow-hidden transition-all duration-700 ease-[0.32,0.72,0,1] ${isDateAnimating ? 'translate-y-0 md:scale-100' : 'translate-y-full md:scale-95'}`}>
                        <div className="w-12 h-1.5 bg-slate-100 rounded-full mx-auto my-4 md:hidden"></div>
                        <div className="p-8">
                            <div className="flex items-center justify-between mb-8">
                                <button onClick={() => { const next = new Date(pickerMonth.getFullYear(), pickerMonth.getMonth() - 1); setPickerMonth(next); }} className="p-2 md:hover:bg-slate-50 rounded-xl transition-colors text-slate-400"><ChevronLeft size={20}/></button>
                                <span className="font-black text-slate-900 text-sm uppercase tracking-widest">{pickerMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                                <button onClick={() => { const next = new Date(pickerMonth.getFullYear(), pickerMonth.getMonth() + 1); setPickerMonth(next); }} className="p-2 md:hover:bg-slate-50 rounded-xl transition-colors text-slate-400"><ChevronRight size={20}/></button>
                            </div>
                            <div className="grid grid-cols-7 gap-1 text-center mb-4">{['S','M','T','W','T','F','S'].map((d, i) => <span key={`${d}-${i}`} className="text-[10px] font-black text-slate-300 uppercase">{d}</span>)}</div>
                            <div className="grid grid-cols-7 gap-1">{(() => { const year = pickerMonth.getFullYear(); const month = pickerMonth.getMonth(); const firstDay = new Date(year, month, 1).getDay(); const daysInMonth = new Date(year, month + 1, 0).getDate(); const today = getLocalISODate(); const days = []; for (let i = 0; i < firstDay; i++) days.push(<div key={`empty-${i}`} className="h-10"></div>); for (let d = 1; d <= daysInMonth; d++) { const dateStr = getLocalISODate(new Date(year, month, d)); const isSelected = dateStr === selectedDate; const isToday = dateStr === today; const hasMatches = allGames.some(g => g.dateLabel === dateStr); days.push(<button key={d} onClick={() => handleDateSelect(dateStr)} className={`h-10 w-full rounded-xl text-xs font-black transition-all relative ${isSelected ? 'bg-slate-900 text-white' : isToday ? 'text-rose-500 bg-rose-50' : 'text-slate-600 md:hover:bg-slate-50'}`}>{d}{hasMatches && !isSelected && <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-rose-500/40"></div>}</button>); } return days; })()}</div>
                        </div>
                        <div className="p-6 bg-slate-50 border-t border-slate-100 pb-10 md:pb-6"><button onClick={() => handleDateSelect(getLocalISODate())} className="w-full py-4 bg-white border border-slate-200 rounded-2xl text-[11px] font-black text-slate-600 uppercase tracking-widest md:hover:bg-slate-900 md:hover:text-white md:hover:border-slate-900 transition-all shadow-sm">{t(lang, 'goToToday')}</button></div>
                    </div>
                </div>
            )}
            {isLeagueSwitcherOpen && (
                <div className={`fixed inset-0 z-[300] flex items-end sm:items-center justify-center transition-all duration-500 ${isSwitcherAnimating ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={closeLeagueSwitcher}></div>
                    <div className={`relative overflow-hidden bg-white w-full md:max-w-sm rounded-t-[40px] sm:rounded-[40px] shadow-2xl flex flex-col transition-all duration-700 ease-[0.32,0.72,0,1] ${isSwitcherAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                        <div className="w-12 h-1.5 bg-slate-100 rounded-full mx-auto my-4 sm:hidden"></div>
                        <div className="px-8 pt-0 md:pt-6 pb-4 flex justify-between items-center border-b border-slate-50">
                            <div className="flex flex-col">
                                <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight leading-none">{t(lang, 'activeSource')}</h2>
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">{t(lang, 'configureIntelSources')}</span>
                            </div>
                            <button onClick={closeLeagueSwitcher} className="p-2 text-slate-300 md:hover:text-slate-900 transition-colors bg-slate-50 rounded-full">
                                <X size={20} strokeWidth={3} />
                            </button>
                        </div>
                        <div className="overflow-y-auto px-4 py-6 max-h-[50vh] custom-scrollbar flex flex-col gap-2">
                            <button onClick={() => setPendingLeagueId('all')} className={`relative flex items-center gap-4 p-4 rounded-2xl transition-all border ${pendingLeagueId === 'all' ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-100 md:hover:border-slate-200 md:hover:bg-slate-50 text-slate-900'}`}>
                                {pendingLeagueId === 'all' && <div className="absolute left-0 top-3 bottom-3 w-1 bg-rose-500 rounded-r-full" />}
                                <div className="w-10 h-10 rounded-xl bg-white p-1.5 flex items-center justify-center shrink-0 border border-slate-100 shadow-sm">
                                    <Globe size={18} className="text-slate-900" />
                                </div>
                                <div className="flex-1 text-left min-w-0">
                                    <span className={`font-black text-xs uppercase tracking-tight truncate block ${pendingLeagueId === 'all' ? 'text-white' : 'text-slate-900'}`}>{t(lang, 'allLeagues')}</span>
                                </div>
                                {pendingLeagueId === 'all' && <Check size={16} strokeWidth={3} className="text-rose-500 shrink-0" />}
                            </button>
                            
                            <Reorder.Group axis="y" values={tempOrderedLeagues} onReorder={handleReorder} className="flex flex-col gap-2">
                                {tempOrderedLeagues.map(l => (
                                    <LeagueReorderItem 
                                        key={l.id} 
                                        league={l} 
                                        pendingLeagueId={pendingLeagueId} 
                                        onSelect={setPendingLeagueId} 
                                    />
                                ))}
                            </Reorder.Group>
                        </div>
                        <div className="p-8 mt-auto border-t border-slate-50 bg-slate-50/50">
                            <button 
                                onClick={handleConfirmActiveLeague} 
                                disabled={isSaving}
                                className="w-full py-4 bg-slate-900 text-white border-none rounded-2xl text-[11px] font-black uppercase tracking-widest md:hover:bg-rose-500 transition-all shadow-sm shadow-slate-200 active:scale-95 flex items-center justify-center gap-2"
                            >
                                {isSaving ? <Loader2 className="animate-spin" size={16} /> : t(lang, 'selectionDone')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
